import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { alertServerError, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { IThemeDTO, IThemesDAO } from '../../../../../../api/themes.dao';
import { IEventTitleAndDescription, IPassportSettingsConfigurationDAO, IPassportSettingsConfigurationDTO } from '../../../../../../api/passport-settings-configuration.dao';
import { getWebsiteFieldValidationError } from '../../../../../../infrastructure/utils/getWebsiteFieldValidationError';
import {
    CUSTOM,
    DEFAULT,
    LONG_RICH_TEXT_LENGTH,
    MAX_ACCESS_CODE_LENGTH,
    MIN_ACCESS_CODE_LENGTH,
    OFFSET_WITH_GEOGRAPHY,
    RADIX_HEX_PARAM,
    RADIX_PARAM,
    SHORT_PLAIN_TEXT_LENGTH,
    SHORT_RICH_TEXT_LENGTH
} from '../../../../../../constants/common.constants';
import {
    MAGIC_LINKS_VALIDITY_DAYS_CANT_BE_GREATER_THAN_99,
    MAGIC_LINKS_VALIDITY_DAYS_CANT_BE_LOWER_THAN_0,
    MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE,
    MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE,
    MAX_SHORT_RICH_TEXT_LENGTH_ERROR_MESSAGE,
    REQUIRED_ERROR_MESSAGE
} from '../../../../../../constants/validation-errors';
import { calcByteCodeSize } from '../../../../../../infrastructure/utils/common.utils';
import { MagicLinkValidityPeriodStrategy } from '../../../../../../api/website.dao';
import { HEX_COLOR_REG_EXP, RGB_COLOR_REG_EXP } from '../../../../../../constants/regex.constants';
import OE_Logo from '../../../../../../images/svg/passport-logo';
import UploadImageS3VM from '../../../shared/upload-image/upload-image-to-s3.viewmodel';
import {
    convertBoolToDefaultOrCustomTypeForMixPanel,
    convertBoolTypeForMixPanel,
    handleTrackingValueChanges,
    isTrackingFieldEnabled,
    LogoType,
    TRACKING_EVENTS
} from '../../../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { accessCodeStatus, calendarInvitesOptions, signInLandings, timeZoneDisplayTypeLabels } from './passport-settings.constants';
import { IImageWithObjectKey } from '../../../../../../api/shared.dto';
import { IEventsDAO } from '../../../../../../api/events.dao';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../global/rich-text-editor/utils/transpiler';
import { FORM_UPDATED_TEXT } from '../../../../../../constants/common-labels.constants';
import { UploadImageS3WithAIViewModel } from './components/upload-image-to-s3-ai/upload-image-to-s3-ai.viewmodel';
import { IdentityProviderMapping, IdentityProviderResponseDAO, IEventIdentityProviderMapping, IPassportAttendeeSSODAO } from '../../../../../../api/passport-attendee-sso.dao';
import { IDP_REQUIRED_TEXT } from './passport-settings-form.texts';

interface ISSOProviderConfiguration extends IdentityProviderMapping {
    eventIdpId?: number;
    isExistingEventIdp?: boolean;
}

const defaultValues: IPassportSettingsConfigurationDTO = {
    subdomain: '',
    themeId: null,
    eventTitleForPassport: '',
    eventPublicDescription: '',
    eventPrivateDescription: '',
    privacyPolicyLabel: '',
    signInLandingWith: '',
    predefinedAccessCode: null,
    requestAccessCodeAutoSingInEnabled: false,
    privacyPolicyEnabled: false,
    magicLinkBuildStrategy: MagicLinkValidityPeriodStrategy.CURRENT_DATE,
    magicLinkValidityDays: 0,
    primaryColor: '',
    logoImage: {
        title: '',
        objectKey: ''
    },
    logoImageWithUrl: {
        title: '',
        assetUrl: ''
    },
    logoAltText: '',
    backgroundImage: {
        title: '',
        objectKey: ''
    },
    backgroundImageWithUrl: {
        title: '',
        assetUrl: ''
    },
    backgroundAltText: '',
    showCookieNotification: false,
    cookieNotificationText: '<p>This site uses only essential cookies stored on your device ' +
        'that are necessary for the site to function. <a target="_blank" href="https://www.openexc.com/privacy-policy/">Cookie Policy</a></p>',
    websiteLogoImage: {
        title: '',
        objectKey: ''
    },
    websiteLogoImageWithUrl: {
        title: '',
        assetUrl: ''
    },
    websiteLogoAltText: '',
    showWebsiteLogoImage: false,
    websiteFooterLogoImage: {
        title: '',
        objectKey: ''
    },
    websiteFooterLogoImageWithUrl: {
        title: '',
        assetUrl: ''
    },
    showWebsiteFooterLogoImage: true,
    timeZoneDisplayType: timeZoneDisplayTypeLabels.default.value,
    customTimeZoneDisplay: '',
    showEventTimeZone: false,
    calendarInvite: calendarInvitesOptions.downloadCalendarFiles.value,
    calendarButtonOnMeetingDetailsPageEnabled: true,
    attendeeSsoEnabled: false,
};



const getDefaultImageToggleLabel = (intl: IntlShape, key: string, disabled: boolean): string => {
    return intl.formatMessage({
        description: 'Label text',
        defaultMessage: disabled ? `OpenExchange logo for ${key} bar is disabled` : `Use OpenExchange logo for ${key} bar`,
        id: disabled ? `oe.websiteTab.useOpenExchangeLogo${key}BarDisabled` : `oe.websiteTab.useOpenExchangeLogo${key}Bar`
    });
};

export default class PassportSettingsFormVM {
    @mobx.observable
    public isSaving: boolean;

    @mobx.observable
    public isLoading: boolean;

    @mobx.observable
    public subdomain: string;

    @mobx.observable
    public themeList: IThemeDTO[];

    @mobx.observable
    public activeThemeId: number;

    @mobx.observable
    public eventTitleForPassport: string;

    @mobx.observable
    public eventPublicDescription: string;

    @mobx.observable
    public eventPrivateDescription: string;

    @mobx.observable
    public privacyPolicyLabel: string;

    @mobx.observable
    public signInLandingWith: string;

    @mobx.observable
    public accessCodeStatus: string;

    @mobx.observable
    public selectedCalendarInvite: string;

    @mobx.observable
    public predefinedAccessCode: string | null;

    @mobx.observable
    public predefinedAccessCodeValidationError: string | null;

    @mobx.observable
    public timeZoneDisplayType: string;

    @mobx.observable
    public customTimeZoneDisplay: string | null;

    @mobx.observable
    public magicLinkBuildStrategy: MagicLinkValidityPeriodStrategy;

    @mobx.observable
    public magicLinkValidityDays: number;

    @mobx.observable
    public requestAccessCodeAutoSingInEnabled: boolean;

    @mobx.observable
    public privacyPolicyEnabled: boolean;

    @mobx.observable
    public showCookieNotification: boolean;

    @mobx.observable
    public cookieNotificationText: string;

    @mobx.observable
    public primaryColor: string;

    @mobx.observable
    public uploadLogoImageS3VM: UploadImageS3VM = null;

    @mobx.observable
    public uploadBackgroundImageS3VM: UploadImageS3VM = null;

    @mobx.observable
    public uploadWebsiteLogoS3VM: UploadImageS3VM = null;

    @mobx.observable
    public uploadFooterLogoS3VM: UploadImageS3VM = null;

    @mobx.observable
    public websiteLogoImageAltText: string;

    @mobx.observable
    public logoImageAltText: string;

    @mobx.observable
    public backgroundImageAltText: string;

    private cachedPassportSettingsConfiguration: IPassportSettingsConfigurationDTO;
    @mobx.observable
    public showEventTimeZone: boolean;
    @mobx.observable
    public attendeeSSOEnabled: boolean;
    @mobx.observable
    public eventIdps: IEventIdentityProviderMapping[] = [];
    @mobx.observable
    public accountIdps: IdentityProviderResponseDAO[] = [];
    @mobx.observable
    public selectedIdps: ISSOProviderConfiguration[] = [];
    @mobx.observable
    private initialIdpsList: ISSOProviderConfiguration[] = [];
    @mobx.observable
    public createIdps: { enabled: boolean; allowedIdpIds: string[] }[] = [];
    @mobx.observable
    public updateIdps: { id: number; enabled: boolean; allowedIdpIds: string[] }[] = [];
    @mobx.observable
    private hasExistingEventIdpData: boolean = false;
    @mobx.observable
    private existingEventIdpId: number | null = null;
    @mobx.observable
    public idpConfigurationValidationError: string | null;
    @mobx.observable
    public showCalendarButton: boolean;
    @mobx.observable
    public isLoadingIdps: boolean = false;

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly accountSubdomain: string | null,
        private readonly setSubdomainInPassportTab: (value: string) => void,
        private readonly intl: IntlShape,
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly passportSettingsConfigurationDAO: IPassportSettingsConfigurationDAO = IoC.Container.get(IPassportSettingsConfigurationDAO),
        @IoC.Inject private readonly themeDAO: IThemesDAO = IoC.Container.get(IThemesDAO),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly eventsDAO = IoC.Container.get(IEventsDAO),
        @IoC.Inject private readonly passportAttendeeSSODAO: IPassportAttendeeSSODAO = IoC.Container.get(IPassportAttendeeSSODAO),
    ) {
        this.isSaving = false;
        this.isLoading = true;
        this.themeList = [];
        this.subdomain = defaultValues.subdomain;
        this.activeThemeId = defaultValues.themeId;
        this.eventTitleForPassport = defaultValues.eventTitleForPassport;
        this.eventPublicDescription = defaultValues.eventPublicDescription;
        this.eventPrivateDescription = defaultValues.eventPrivateDescription;
        this.privacyPolicyLabel = defaultValues.privacyPolicyLabel;
        this.signInLandingWith = signInLandings.haveAccessCode.value;
        this.predefinedAccessCodeValidationError = '';
        this.accessCodeStatus = '';
        this.predefinedAccessCode = defaultValues.predefinedAccessCode;
        this.timeZoneDisplayType = defaultValues.timeZoneDisplayType;
        this.customTimeZoneDisplay = defaultValues.customTimeZoneDisplay;
        this.requestAccessCodeAutoSingInEnabled = defaultValues.requestAccessCodeAutoSingInEnabled;
        this.privacyPolicyEnabled = defaultValues.privacyPolicyEnabled;
        this.showCookieNotification = defaultValues.showCookieNotification;
        this.showEventTimeZone = defaultValues.showEventTimeZone;
        this.attendeeSSOEnabled = defaultValues.attendeeSsoEnabled;
        this.idpConfigurationValidationError = '';
        this.cookieNotificationText = defaultValues.cookieNotificationText;
        this.magicLinkBuildStrategy = defaultValues.magicLinkBuildStrategy;
        this.magicLinkValidityDays = defaultValues.magicLinkValidityDays;
        this.primaryColor = defaultValues.primaryColor;
        this.selectedCalendarInvite = defaultValues.calendarInvite;
        this.showCalendarButton = defaultValues.calendarButtonOnMeetingDetailsPageEnabled;
        this.websiteLogoImageAltText = defaultValues.websiteLogoAltText;
        this.logoImageAltText = defaultValues.logoAltText;
        this.backgroundImageAltText = defaultValues.backgroundAltText;
        this.uploadLogoImageS3VM = new UploadImageS3VM(this.accountId, this.eventId, 'logoImage', 'passport');
        this.uploadBackgroundImageS3VM = hasPermission(Permission.GENERATE_IMAGES_WITH_AI)
            ? new UploadImageS3WithAIViewModel(this.accountId, this.eventId, 'backgroundImage', 'passport')
            : new UploadImageS3VM(this.accountId, this.eventId, 'backgroundImage', 'passport');
        this.uploadWebsiteLogoS3VM = new UploadImageS3VM(
            this.accountId,
            this.eventId,
            'websiteLogoImage',
            'passport',
            this.intl.formatMessage({
                description: 'Text message',
                defaultMessage: 'Logos on a transparent background with aspect ratio approx 7:1 work best in the menu bar.',
                id: 'oe.image.websiteLogoImageDescription'
            }),
            {
                imageTitle: 'OpenExchange Logo',
                ImgSrc: OE_Logo,
                showImageByDefault: defaultValues.showWebsiteLogoImage,
                defaultLogoToggleLabel: getDefaultImageToggleLabel(this.intl, 'Menu', false),
                defaultLogoToggleLabelDisabled: getDefaultImageToggleLabel(this.intl, 'Menu', true),
            },
        );
        this.uploadFooterLogoS3VM = new UploadImageS3VM(
            this.accountId,
            this.eventId,
            'websiteFooterLogoImage',
            'passport',
            null,
            {
                imageTitle: 'Powered by OEC',
                ImgSrc: OE_Logo,
                showImageByDefault: defaultValues.showWebsiteFooterLogoImage,
                defaultLogoToggleLabel: getDefaultImageToggleLabel(this.intl, 'Footer', false),
                defaultLogoToggleLabelDisabled: getDefaultImageToggleLabel(this.intl, 'Footer', true),
            },
        );
    }

    public initialize = async () => {
        if (hasPermission(Permission.PASSPORT_TAB)) {
            await this.fetchThemes();
            if (this.canViewAttendeeSsoSection()) {
                await this.loadIdps();
            }
        }
        await this.getPassportSettingsConfiguration();
    };


    @mobx.action
    public setSubdomain = (subdomain: string): void => {
        this.subdomain = subdomain;
        if (!this.subdomain.length) {
            this.setTheme(null);
        }
    };

    public getAccountSubdomain(): string | null {
        return this.accountSubdomain;
    };

    @mobx.action
    private fetchThemes = async (): Promise<void> => {
        try {
            this.themeList = await this.themeDAO.getThemeList({ accountId: this.accountId });
        } catch (e) {
            PassportSettingsFormVM.handleApiException(e);
        }
    };

    @mobx.action
    public setTheme = (selectedThemeId: number | null): void => {
        this.activeThemeId = selectedThemeId;
    };

    @mobx.action
    public setEventTitleForPassport = (message: string): void => {
        this.eventTitleForPassport = message;
    };

    public getEventTitleForPassportError = (): string => {
        if (!this.eventTitleForPassport) return null;
        if (this.eventTitleForPassport.length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }
    };

    @mobx.action
    public setPublicDescription = (message: string): void => {
        this.eventPublicDescription = message;
    };

    public getPublicDescriptionError = (): string => {
        if (!this.eventPublicDescription) return null;
        if (calcByteCodeSize(this.eventPublicDescription) > LONG_RICH_TEXT_LENGTH) {
            return MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
    };

    @mobx.action
    public setPrivateDescription = (message: string): void => {
        this.eventPrivateDescription = message;
    };

    @mobx.action
    public setPrivacyPolicyLabel = (message: string): void => {
        this.privacyPolicyLabel = message;
    };

    @mobx.action
    public setCookieNotificationText = (message: string): void => {
        this.cookieNotificationText = message;
    };

    public getPrivateDescriptionError = (): string => {
        if (!this.eventPrivateDescription) return null;
        if (calcByteCodeSize(this.eventPrivateDescription) > LONG_RICH_TEXT_LENGTH) {
            return MAX_LONG_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
    };

    public getPrivacyPolicyLabelError = (): string => {
        if (!this.privacyPolicyLabel) return null;
        if (calcByteCodeSize(this.privacyPolicyLabel) > SHORT_RICH_TEXT_LENGTH) {
            return MAX_SHORT_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
    };

    public getCookieNotificationTextError = (): string => {
        if (!this.showCookieNotification) return null;
        if (!this.cookieNotificationText) return REQUIRED_ERROR_MESSAGE;
        if (calcByteCodeSize(this.cookieNotificationText) > SHORT_RICH_TEXT_LENGTH) {
            return MAX_SHORT_RICH_TEXT_LENGTH_ERROR_MESSAGE;
        }
    };

    @mobx.action
    public setSignInLanding = (value: string): void => {
        this.signInLandingWith = value;
    };

    @mobx.action
    public setAccessCodeStatus = (value: string): void => {
        this.accessCodeStatus = value;
        if (value === accessCodeStatus.randomlyGenerated.value) {
            this.predefinedAccessCode = null;
            this.predefinedAccessCodeValidationError = null;
        }
    };

    @mobx.action
    public setPredefinedAccessCode = (value: string): void => {
        this.predefinedAccessCode = value.split(' ').join('');
        this.checkPredefinedAccessCodeError();
    };

    @mobx.action
    public setCalendarInviteStatus = (value: string): void => {
        this.selectedCalendarInvite = value;
    };

    @mobx.action
    public toggleShowCalendarButton = (): void => {
        this.showCalendarButton = !this.showCalendarButton;
    };

    private checkPredefinedAccessCodeError = () => {
        if (this.accessCodeStatus === accessCodeStatus.predefined.value &&
            !this.predefinedAccessCode?.length) {
            this.predefinedAccessCodeValidationError = this.getRequiredError('Predefined Access Code');
        } else if (this.accessCodeStatus === accessCodeStatus.predefined.value &&
            this.predefinedAccessCode?.trim()?.length < MIN_ACCESS_CODE_LENGTH || this.predefinedAccessCode?.length > MAX_ACCESS_CODE_LENGTH) {
            this.predefinedAccessCodeValidationError = this.intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Access code length must be between {min} and {max} characters',
                id: 'oe.common.validationLengthAccessCode'
            }, {
                min: MIN_ACCESS_CODE_LENGTH,
                max: MAX_ACCESS_CODE_LENGTH
            });
        } else this.predefinedAccessCodeValidationError = null;
    };

    @mobx.action
    public setTimeZoneDisplayType = (value: string): void => {
        this.timeZoneDisplayType = value;
        if (value !== timeZoneDisplayTypeLabels.custom.value) {
            this.customTimeZoneDisplay = '';
        }
    };

    @mobx.action
    public setCustomTimeZoneDisplay = (value: string): void => {
        this.customTimeZoneDisplay = value;
    };

    public getCustomTimeZoneDisplayError = (): string | null => {
        if (this.timeZoneDisplayType === timeZoneDisplayTypeLabels.custom.value && this.customTimeZoneDisplay.length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        } else return null;
    };

    @mobx.action
    public setWebsiteLogoImageAltText = (value: string): void => {
        this.websiteLogoImageAltText = value;
    };

    @mobx.action
    public setLogoImageAltText = (value: string): void => {
        this.logoImageAltText = value;
    };

    @mobx.action
    public setBackgroundImageAltText = (value: string): void => {
        this.backgroundImageAltText = value;
    };

    public getWebsiteLogoImageAltTextError = (): string | null => {
        return this.getAltTextError(this.websiteLogoImageAltText);
    };

    public getLogoImageAltTextError = (): string | null => {
        return this.getAltTextError(this.logoImageAltText);
    };

    public getBackgroundImageAltTextError = (): string | null => {
        return this.getAltTextError(this.backgroundImageAltText);
    };

    private getAltTextError = (value: string | null): string | null => {
        if (value && value.length > SHORT_PLAIN_TEXT_LENGTH) {
            return MAX_LENGTH_NOT_REQUIRED_ERROR_MESSAGE;
        }
        return null;
    };

    @mobx.action
    public setMagicLinkValidityDays = e => {
        this.magicLinkValidityDays = e.currentTarget.value |= 0;
    };

    public getMagicLinkValidityDaysValidationError = (): string | null => {
        if (this.magicLinkValidityDays < 0) return MAGIC_LINKS_VALIDITY_DAYS_CANT_BE_LOWER_THAN_0;
        if (this.magicLinkValidityDays > 99) return MAGIC_LINKS_VALIDITY_DAYS_CANT_BE_GREATER_THAN_99;
        return null;
    };

    @mobx.action
    public setMagicLinkValidityPeriodStrategy = (value: MagicLinkValidityPeriodStrategy): void => {
        if (value === MagicLinkValidityPeriodStrategy.CURRENT_DATE) this.magicLinkValidityDays = 0;
        this.magicLinkBuildStrategy = value;
    };

    @mobx.action
    public toggleRequestAccessCodeAutoSingIn = (): void => {
        this.requestAccessCodeAutoSingInEnabled = !this.requestAccessCodeAutoSingInEnabled;
    };

    @mobx.action
    public toggleSetPrivacyPolicy = (): void => {
        this.privacyPolicyEnabled = !this.privacyPolicyEnabled;
    };

    @mobx.action
    public toggleShowCookieNotification = (): void => {
        this.showCookieNotification = !this.showCookieNotification;
    };

    @mobx.action
    public setPrimaryColor = (color: string): void => {
        this.primaryColor = color;
    };

    public getNormalizedPrimaryColor = (): string => {
        if (!this.getColorValidationError()) {
            if (this.primaryColor.match(RGB_COLOR_REG_EXP)) {
                const rgbColorString = this.primaryColor.slice(4, -1);
                const rgbColors = rgbColorString.split(', ');
                let hexColorString = '#';
                rgbColors.forEach((c: string) => {
                    hexColorString += parseInt(c, RADIX_PARAM).toString(RADIX_HEX_PARAM).padStart(2, '0');
                });
                return hexColorString;
            }
            return this.primaryColor;
        }
        return null;
    };

    public getColorValidationError = (): string | null => {
        if (this.primaryColor && !(this.primaryColor.match(RGB_COLOR_REG_EXP) || this.primaryColor.match(HEX_COLOR_REG_EXP))) {
            return this.intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Primary color should be in HEX - #000000 or RGB - rgb(0, 0, 255) format',
                id: 'oe.common.validationPrimaryColor'
            });
        }
        return null;
    };


    public isImageUploading = (): boolean => {
        return this.uploadLogoImageS3VM.isImageUploading
            || this.uploadBackgroundImageS3VM.isImageUploading
            || this.uploadWebsiteLogoS3VM.isImageUploading
            || this.uploadFooterLogoS3VM.isImageUploading;
    };


    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    private getRequiredError = (fieldLabel: string): string => {
        return this.intl.formatMessage({
            description: 'Warning message',
            defaultMessage: '{field} is required',
            id: 'oe.common.fieldIsRequired'
        }, {
            field: fieldLabel
        });
    };

    @mobx.action
    private getPassportSettingsConfiguration = async (): Promise<void> => {
        try {
            const result = await this.passportSettingsConfigurationDAO.getPassportSettingsConfiguration({ eventId: this.eventId, accountId: this.accountId });
            this.cachedPassportSettingsConfiguration = result;
            const {
                subdomain,
                themeId,
                eventTitleForPassport,
                eventPublicDescription,
                eventPrivateDescription,
                privacyPolicyLabel,
                signInLandingWith,
                predefinedAccessCode,
                requestAccessCodeAutoSingInEnabled,
                privacyPolicyEnabled,
                magicLinkBuildStrategy,
                magicLinkValidityDays,
                primaryColor,
                logoImageWithUrl,
                logoImage,
                logoAltText,
                backgroundImageWithUrl,
                backgroundImage,
                backgroundAltText,
                showCookieNotification,
                cookieNotificationText,
                websiteLogoImage,
                websiteLogoImageWithUrl,
                websiteLogoAltText,
                showWebsiteLogoImage,
                websiteFooterLogoImage,
                websiteFooterLogoImageWithUrl,
                showWebsiteFooterLogoImage,
                timeZoneDisplayType,
                customTimeZoneDisplay,
                showEventTimeZone,
                calendarInvite,
                calendarButtonOnMeetingDetailsPageEnabled,
                attendeeSsoEnabled,
            } = result;

            mobx.runInAction(() => {
                this.subdomain = subdomain || defaultValues.subdomain;
                this.activeThemeId = themeId || defaultValues.themeId;
                this.eventTitleForPassport = eventTitleForPassport || defaultValues.eventTitleForPassport;
                this.eventPublicDescription = transpileInlineStylesToClasses(eventPublicDescription) || defaultValues.eventPublicDescription;
                this.eventPrivateDescription = transpileInlineStylesToClasses(eventPrivateDescription) || defaultValues.eventPrivateDescription;
                this.privacyPolicyLabel = transpileInlineStylesToClasses(privacyPolicyLabel) || defaultValues.privacyPolicyLabel;
                this.signInLandingWith = signInLandingWith || signInLandings.haveAccessCode.value;
                this.accessCodeStatus = predefinedAccessCode ? accessCodeStatus.predefined.value : accessCodeStatus.randomlyGenerated.value;
                this.predefinedAccessCode = predefinedAccessCode || defaultValues.predefinedAccessCode;
                this.requestAccessCodeAutoSingInEnabled = requestAccessCodeAutoSingInEnabled;
                this.privacyPolicyEnabled = privacyPolicyEnabled;
                this.showCookieNotification = showCookieNotification;
                this.cookieNotificationText = transpileInlineStylesToClasses(cookieNotificationText) || defaultValues.cookieNotificationText;
                this.magicLinkBuildStrategy = magicLinkBuildStrategy || defaultValues.magicLinkBuildStrategy;
                this.magicLinkValidityDays = magicLinkValidityDays || defaultValues.magicLinkValidityDays;
                this.primaryColor = primaryColor || defaultValues.primaryColor;
                this.uploadLogoImageS3VM.addImage(logoImageWithUrl?.assetUrl, logoImage?.title, logoImage?.objectKey);
                this.uploadBackgroundImageS3VM.addImage(backgroundImageWithUrl?.assetUrl, backgroundImage?.title, backgroundImage?.objectKey);
                this.uploadWebsiteLogoS3VM.addImage(websiteLogoImageWithUrl?.assetUrl, websiteLogoImage?.title, websiteLogoImage?.objectKey);
                this.uploadWebsiteLogoS3VM.toggleShowDefaultImage(showWebsiteLogoImage);
                this.uploadFooterLogoS3VM.addImage(websiteFooterLogoImageWithUrl?.assetUrl, websiteFooterLogoImage?.title, websiteFooterLogoImage?.objectKey);
                this.uploadFooterLogoS3VM.toggleShowDefaultImage(showWebsiteFooterLogoImage);
                this.websiteLogoImageAltText = websiteLogoAltText || '';
                this.logoImageAltText = logoAltText || '';
                this.backgroundImageAltText = backgroundAltText || '';
                this.timeZoneDisplayType = timeZoneDisplayType || defaultValues.timeZoneDisplayType;
                this.customTimeZoneDisplay = customTimeZoneDisplay || defaultValues.customTimeZoneDisplay;
                this.showEventTimeZone = showEventTimeZone;
                this.selectedCalendarInvite = calendarInvite || defaultValues.calendarInvite;
                this.showCalendarButton = calendarButtonOnMeetingDetailsPageEnabled;
                this.attendeeSSOEnabled = attendeeSsoEnabled;
            });
        } catch (e) {
            PassportSettingsFormVM.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    public checkValidity = (): boolean => {
        this.checkPredefinedAccessCodeError();
        if (!getWebsiteFieldValidationError(this.subdomain)
            && !this.getEventTitleForPassportError()
            && !this.getPublicDescriptionError()
            && !this.getPrivateDescriptionError()
            && !this.predefinedAccessCodeValidationError
            && !this.getMagicLinkValidityDaysValidationError()
            && !this.getColorValidationError()
            && !this.getCookieNotificationTextError()
            && !this.getCustomTimeZoneDisplayError()
            && !this.getWebsiteLogoImageAltTextError()
            && !this.getLogoImageAltTextError()
            && !this.getBackgroundImageAltTextError()
            && !this.getIdpSSODisplayError()
        ) return true;
        else return false;
    };

    @mobx.action
    public setUpdatedData = (updatedData: IPassportSettingsConfigurationDTO) => {
        if (updatedData) {
            const {
                subdomain = '',
                backgroundImage = defaultValues.backgroundImage,
                backgroundImageWithUrl = defaultValues.backgroundImageWithUrl,
                logoImage = defaultValues.logoImage,
                logoImageWithUrl = defaultValues.logoImageWithUrl,
                logoAltText = defaultValues.logoAltText,
                websiteLogoImage = defaultValues.logoImage,
                websiteLogoImageWithUrl = defaultValues.logoImageWithUrl,
                websiteLogoAltText = defaultValues.websiteLogoAltText,
                backgroundAltText = defaultValues.backgroundAltText,
                websiteFooterLogoImage = defaultValues.logoImage,
                websiteFooterLogoImageWithUrl = defaultValues.logoImageWithUrl,
                calendarButtonOnMeetingDetailsPageEnabled = defaultValues.calendarButtonOnMeetingDetailsPageEnabled,
            } = updatedData;

            this.setSubdomainInPassportTab(subdomain);
            this.uploadLogoImageS3VM.addImage(logoImageWithUrl?.assetUrl, logoImage?.title, logoImage?.objectKey);
            this.uploadBackgroundImageS3VM.addImage(backgroundImageWithUrl?.assetUrl, backgroundImage?.title, backgroundImage?.objectKey);
            this.uploadWebsiteLogoS3VM.addImage(websiteLogoImageWithUrl?.assetUrl, websiteLogoImage?.title, websiteLogoImage?.objectKey);
            this.uploadFooterLogoS3VM.addImage(websiteFooterLogoImageWithUrl?.assetUrl, websiteFooterLogoImage?.title, websiteFooterLogoImage?.objectKey);
            this.websiteLogoImageAltText = websiteLogoAltText;
            this.logoImageAltText = logoAltText;
            this.backgroundImageAltText = backgroundAltText;
            this.showCalendarButton = calendarButtonOnMeetingDetailsPageEnabled;
        }
    };

    @mobx.action
    public applyPassportSettingsForm = async (): Promise<boolean> => {
        try {
            if (!this.checkValidity()) return false;
            this.isSaving = true;
            const logoImage = this.uploadLogoImageS3VM.getImageToApi();
            const backgroundImage = this.uploadBackgroundImageS3VM.getImageToApi();
            const websiteLogoImage = this.uploadWebsiteLogoS3VM.getImageToApi();
            const footerLogoImage = this.uploadFooterLogoS3VM.getImageToApi();

            const updatePassportSettingsDTO: IPassportSettingsConfigurationDTO = {
                subdomain: this.subdomain || null,
                themeId: this.activeThemeId,
                eventTitleForPassport: this.eventTitleForPassport || null,
                eventPublicDescription: transpileClassesToInlineStyles(this.eventPublicDescription) || null,
                eventPrivateDescription: transpileClassesToInlineStyles(this.eventPrivateDescription) || null,
                privacyPolicyLabel: transpileClassesToInlineStyles(this.privacyPolicyLabel) || null,
                signInLandingWith: this.signInLandingWith,
                ...(this.predefinedAccessCode && { predefinedAccessCode: this.predefinedAccessCode }),
                requestAccessCodeAutoSingInEnabled: this.requestAccessCodeAutoSingInEnabled,
                privacyPolicyEnabled: this.privacyPolicyEnabled,
                magicLinkBuildStrategy: this.magicLinkBuildStrategy,
                ...(this.magicLinkBuildStrategy === MagicLinkValidityPeriodStrategy.EVENT_END_DATE &&
                    { magicLinkValidityDays: this.magicLinkValidityDays }
                ),
                primaryColor: this.primaryColor || null,
                ...logoImage,
                ...backgroundImage,
                ...websiteLogoImage,
                ...footerLogoImage,
                logoAltText: this.logoImageAltText || '',
                backgroundAltText: this.backgroundImageAltText || '',
                websiteLogoAltText: this.websiteLogoImageAltText || '',
                showCookieNotification: this.showCookieNotification,
                cookieNotificationText: transpileClassesToInlineStyles(this.cookieNotificationText) || defaultValues.cookieNotificationText,
                showWebsiteLogoImage: this.uploadWebsiteLogoS3VM.showDefaultImage,
                showWebsiteFooterLogoImage: this.uploadFooterLogoS3VM.showDefaultImage,
                timeZoneDisplayType: this.timeZoneDisplayType,
                customTimeZoneDisplay: this.customTimeZoneDisplay || null,
                showEventTimeZone: this.showEventTimeZone,
                calendarInvite: this.selectedCalendarInvite,
                calendarButtonOnMeetingDetailsPageEnabled: this.showCalendarButton,
                attendeeSsoEnabled: this.getisAttendeeSSOLocked() && !this.attendeeSSOEnabled ? this.updateAttendeeSSOToggole() : this.attendeeSSOEnabled,
            };

            const response = await this.passportSettingsConfigurationDAO.updatePassportSettingsConfiguration({
                accountId: this.accountId,
                eventId: this.eventId,
            }, updatePassportSettingsDTO);

            this.trackEventMixPanel(TRACKING_EVENTS.CONFIGURE_PASSPORT_SETTINGS, updatePassportSettingsDTO);

            const hasIdpOperations = this.createIdps.length > 0 || this.updateIdps.length > 0;

            if (hasIdpOperations) {
                try {
                    await this.createIdpsForEvent();
                    await this.updateIdpsForEvent();
                    await this.loadIdps();
                } catch (idpError) {
                    PassportSettingsFormVM.handleApiException(idpError);
                } finally {
                    mobx.runInAction(() => {
                        this.createIdps = [];
                        this.updateIdps = [];
                    });
                }
            }

            this.setUpdatedData(response);
            this.cachedPassportSettingsConfiguration = updatePassportSettingsDTO;

            showNotification(this.intl.formatMessage(FORM_UPDATED_TEXT));
            if (response) {
                const eventDetails = await this.eventsDAO.getEventDetails({ accountId: this.accountId, eventId: this.eventId });
                this.globalStorage.setEvent(eventDetails);
            }
            return true;
        } catch (e) {
            PassportSettingsFormVM.handleApiException(e);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    public convertDataToTrackingService(fieldName: string, updatedData: IPassportSettingsConfigurationDTO): string {
        return convertBoolTypeForMixPanel(!!updatedData[fieldName]);
    }

    public convertImageDataToTrackingService(fieldName: string, updatedData: IPassportSettingsConfigurationDTO): string {
        const isImageUpdated = !!updatedData[fieldName]?.objectKey;
        return convertBoolTypeForMixPanel(isImageUpdated);
    }

    public convertLogoDataToTrackingService(showLogoImage: boolean, uploadedLogoImage: IImageWithObjectKey): LogoType {
        if (showLogoImage) return LogoType.OE_LOGO;
        if (uploadedLogoImage) return LogoType.CUSTOM_LOGO;
        return LogoType.NO_LOGO;
    }

    public trackEventMixPanel = (eventName: string, eventData: IPassportSettingsConfigurationDTO): void => {
        const theme = this.themeList.find(el => el.id === eventData.themeId)?.name || 'Default';
        const signInLandingWith = Object.values(signInLandings).find(el => el.value === eventData.signInLandingWith).name.defaultMessage;
        const accessCode = eventData.predefinedAccessCode ? 'Predefined' : 'Randomly Generated';
        const magicLinkStrategy = eventData.magicLinkBuildStrategy === MagicLinkValidityPeriodStrategy.CURRENT_DATE ?
            'Magic Links valid for 24 hours' : 'Magic Links valid until Event End Date';
        let timeZoneDisplayType = '';
        switch (eventData.timeZoneDisplayType) {
            case timeZoneDisplayTypeLabels.offsetWithGeography.value:
                timeZoneDisplayType = OFFSET_WITH_GEOGRAPHY;
                break;
            case timeZoneDisplayTypeLabels.custom.value:
                timeZoneDisplayType = CUSTOM;
                break;
            default:
                timeZoneDisplayType = DEFAULT;
                break;
        }

        this.trackingService.get().track(eventName, {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Website URL': this.convertDataToTrackingService('subdomain', eventData),
            'Passport Theme': theme,
            'Event Title for Passport': this.convertDataToTrackingService('eventTitleForPassport', eventData),
            'Event Public Description': this.convertDataToTrackingService('eventPublicDescription', eventData),
            'Event Private Description': this.convertDataToTrackingService('eventPrivateDescription', eventData),
            'Sign In Landing with': signInLandingWith,
            'Privacy Policy Checkbox': isTrackingFieldEnabled(eventData.privacyPolicyEnabled),
            'Access Code': accessCode,
            'One-Step Request Access Code and Sign In': isTrackingFieldEnabled(eventData.requestAccessCodeAutoSingInEnabled),
            'Calendar Invite': this.selectedCalendarInvite,
            'Magic Links validity period': magicLinkStrategy,
            'Logo Image': this.convertImageDataToTrackingService('logoImage', eventData),
            'Background Image': this.convertImageDataToTrackingService('backgroundImage', eventData),
            'Menu Bar Logo Image': this.convertLogoDataToTrackingService(eventData.showWebsiteLogoImage, eventData.websiteLogoImage),
            'Primary Color': this.convertDataToTrackingService('primaryColor', eventData),
            'Cookie Notification': isTrackingFieldEnabled(eventData.showCookieNotification),
            'Cookie Notification Text': convertBoolToDefaultOrCustomTypeForMixPanel(eventData.cookieNotificationText === defaultValues.cookieNotificationText),
            'Footer Bar Logo Image': this.convertLogoDataToTrackingService(eventData.showWebsiteFooterLogoImage, eventData.websiteFooterLogoImage),
            'Time Zone Display': timeZoneDisplayType,
            'Display event time zone for all meetings': isTrackingFieldEnabled(eventData.showEventTimeZone),
            'Calendar button on meeting details page': isTrackingFieldEnabled(eventData.calendarButtonOnMeetingDetailsPageEnabled),
        });
    };

    public trackEventTitleAndDescription = (eventName: string, eventData: IEventTitleAndDescription): void => {
        this.trackingService.get().track(eventName, {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Account ID': this.accountId,
            'Event Name': this.globalStorage.getEvent()?.name,
            'Event ID': this.eventId,
            'Event Title': handleTrackingValueChanges(
                this.cachedPassportSettingsConfiguration.eventTitleForPassport,
                eventData.eventTitleForPassport
            ),
            'Public Description': handleTrackingValueChanges(
                this.cachedPassportSettingsConfiguration.eventPublicDescription,
                eventData.eventPublicDescription
            ),
            'Private Description': handleTrackingValueChanges(
                this.cachedPassportSettingsConfiguration.eventPrivateDescription,
                eventData.eventPrivateDescription
            ),
        });
    };

    @mobx.action
    public toggleShowEventTimeZone = (): void => {
        this.showEventTimeZone = !this.showEventTimeZone;
    };

    public getIdpSSODisplayError = (): string | null => {
        if (!this.attendeeSSOEnabled || !hasPermission(Permission.ATTENDEE_SSO)) {
            this.idpConfigurationValidationError = null;
            return null;
        }
        const hasEnabled = this.selectedIdps.some(p => p.enabled || this.getisIdpLocked(p.fusionAuthIdpId));
        const errorMessage = hasEnabled ? null : IDP_REQUIRED_TEXT.defaultMessage;
        this.idpConfigurationValidationError = errorMessage;
        return errorMessage;
    };

    @mobx.action
    public toggleAttendeeSSO = (): void => {
        this.attendeeSSOEnabled = !this.attendeeSSOEnabled;

        if (!this.attendeeSSOEnabled && this.initialIdpsList.length > 0) {
            this.selectedIdps = this.initialIdpsList.map(idp => ({ ...idp }));
            this.createIdps = [];
            this.updateIdps = [];
            this.idpConfigurationValidationError = null;
        }
    };

    @mobx.action
    public toggleSelectedProvider = (fusionAuthIdpId: string): void => {
        const idx = this.selectedIdps.findIndex(i => i.fusionAuthIdpId === fusionAuthIdpId);
        if (idx === -1) return;

        const provider = this.selectedIdps[idx];
        const newEnabled = !provider.enabled;
        this.selectedIdps[idx] = { ...provider, enabled: newEnabled };

        const enabledIdps = this.selectedIdps
            .filter(p => p.enabled)
            .map(p => p.fusionAuthIdpId);

        if (enabledIdps.length > 0) {
            this.idpConfigurationValidationError = '';
        }

        const selectedFusionIdps = this.selectedIdps
            .map(a => a.fusionAuthIdpId)
            .filter(Boolean);

        const filteredExistingEventIdps = this.eventIdps
            .flatMap(item => item.allowedIdpIds)
            .filter(id => !selectedFusionIdps.includes(id));

        const accountFusionIdps = this.accountIdps
            .map(a => a.fusionAuthIdpId)
            .filter(Boolean);

        const existingEventValidIds = filteredExistingEventIdps
            .filter(id => accountFusionIdps.includes(id));

        this.createIdps = [];
        this.updateIdps = [];

        if (this.hasExistingEventIdpData && this.existingEventIdpId) {
            this.updateIdps.push({
                id: this.existingEventIdpId,
                enabled: enabledIdps.length > 0,
                allowedIdpIds: [
                    ...enabledIdps,
                    ...existingEventValidIds
                ],
            });
        } else {
            if (enabledIdps.length > 0) {
                this.createIdps.push({
                    enabled: true,
                    allowedIdpIds: enabledIdps,
                });
            }
        }
    };

    @mobx.action
    public loadIdps = async (): Promise<void> => {
        if (this.isLoadingIdps) return;

        try {
            mobx.runInAction(() => {
                this.isLoadingIdps = true;
            });

            const [accountLevelIdps, eventLevelIdps] = await Promise.all([
                this.passportAttendeeSSODAO.getPassportIdentityProviders({ accountId: this.accountId }),
                this.passportAttendeeSSODAO.getEventPassportIdentityProviders({ eventId: this.eventId, accountId: this.accountId }),
            ]);

            mobx.runInAction(() => {
                this.accountIdps = accountLevelIdps;
                this.eventIdps = eventLevelIdps;
                this.hasExistingEventIdpData = eventLevelIdps.length > 0;
                this.existingEventIdpId = eventLevelIdps.length > 0 && eventLevelIdps[0].id ? eventLevelIdps[0].id : null;

                const filteredAccountIdps = (accountLevelIdps ?? []).filter(acc => acc.enabled);

                const accountMap: ISSOProviderConfiguration[] = filteredAccountIdps.map(acc => {
                    const eventMatch = eventLevelIdps.find(ev =>
                        Array.isArray(ev.allowedIdpIds) && ev.allowedIdpIds.includes(acc.fusionAuthIdpId)
                    );

                    return {
                        fusionAuthIdpId: acc.fusionAuthIdpId,
                        fusionAuthIdpName: acc.fusionAuthIdpName,
                        enabled: !!acc.enabledForAllEvents || !!eventMatch?.enabled,
                        enabledForAllEvents: acc.enabledForAllEvents ?? false,
                        eventIdpId: eventMatch?.id ?? null,
                        isExistingEventIdp: !!eventMatch,
                        managedDomains: acc.managedDomains ?? [],
                    };
                });

                this.selectedIdps = accountMap.sort((a, b) =>
                    a.fusionAuthIdpName.localeCompare(b.fusionAuthIdpName)
                );
                this.initialIdpsList = accountMap.map(idp => ({ ...idp }));
            });
        } catch (err) {
            PassportSettingsFormVM.handleApiException(err);
        } finally {
            mobx.runInAction(() => {
                this.isLoadingIdps = false;
            });
        }
    };

    @mobx.action
    public getPassportAttendeeSSOEnables = (): boolean => {
        return this.globalStorage.getEvent()?.passportAttendeeSSOEnabled ?? false;
    };

    public canViewAttendeeSsoSection = (): boolean => {
        return this.getPassportAttendeeSSOEnables() && hasPermission(Permission.ATTENDEE_SSO);
    };

    @mobx.action
    public getisSSOControlledAtAccountLevel = (): boolean => {
        return this.accountIdps?.some(idp => idp.enabledForAllEvents);
    };

    @mobx.action
    public getisAttendeeSSOLocked = (): boolean => {
        return this.getisSSOControlledAtAccountLevel();
    };

    @mobx.action
    public getisIdpLocked = (fusionAuthIdpId: string): boolean => {
        const isIdpLocked = this.accountIdps?.some(idp => idp.fusionAuthIdpId === fusionAuthIdpId && idp.enabledForAllEvents);
        return isIdpLocked;
    };

    @mobx.action
    public updateAttendeeSSOToggole = (): boolean => {
        const hasValidIdp = [...this.createIdps, ...this.updateIdps].some(obj =>
            Array.isArray(obj.allowedIdpIds) && obj.allowedIdpIds.length > 0
        );
        return hasValidIdp;
    };

    @mobx.action
    public createIdpsForEvent = async (): Promise<void> => {
        if (!this.createIdps.length) return;

        try {
            const createIdpPromises = this.createIdps.map(idp =>
                this.passportAttendeeSSODAO.createEventPassportIdentityProvider({
                    accountId: this.accountId,
                    eventId: this.eventId,
                    enabled: idp.enabled,
                    allowedIdpIds: idp.allowedIdpIds,
                })
            );

            await Promise.all(createIdpPromises);
        } catch (err) {
            PassportSettingsFormVM.handleApiException(err);
        }
    };

    @mobx.action
    public updateIdpsForEvent = async (): Promise<void> => {
        if (!this.updateIdps.length) return;

        try {
            const updateIdpPromises = this.updateIdps.map(idp =>
                this.passportAttendeeSSODAO.updateEventPassportIdentityProvider({
                    accountId: this.accountId,
                    eventId: this.eventId,
                    id: idp.id,
                    enabled: idp.enabled,
                    allowedIdpIds: idp.allowedIdpIds,
                })
            );
            await Promise.all(updateIdpPromises);
        } catch (err) {
            PassportSettingsFormVM.handleApiException(err);
        }
    };
}
